import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import AxiosInstance, { TokenService } from "../../../services/Api";

export default function usePayFeesData() {
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};

  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const class_id = clientData?.class_id || "";
  const section_id = clientData?.section || "";
  const student_id = clientData?.student_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // ------------------------------------------------------------------
  // Turn the columnFilters array into a quick-lookup map:
  //   { due_date: [...], fee_status: "..." }
  // ------------------------------------------------------------------
  const filterMap = useMemo(() => {
    const map = {};
    columnFilters.forEach((f) => {
      if (f?.id !== undefined) map[f.id] = f.value;
    });
    return map;
  }, [columnFilters]);

  const getStr = (id) => {
    const v = filterMap[id];
    if (v === undefined || v === null) return "";
    return String(v).trim();
  };

  // Due Date range filter (column id = "due_date") — expected as [start, end]
  const { startDate, endDate } = useMemo(() => {
    const range = filterMap.due_date;
    if (!Array.isArray(range)) return { startDate: "", endDate: "" };
    return {
      startDate: range[0] ? moment(range[0]).format("YYYY-MM-DD") : "",
      endDate: range[1] ? moment(range[1]).format("YYYY-MM-DD") : "",
    };
  }, [filterMap.due_date]);

  // ------------------------------------------------------------------
  // Build the payload dynamically
  // ------------------------------------------------------------------
  const buildPayload = useCallback(
    () => ({
      school_id: Number(school_id) || school_id,
      academic_year_id: Number(academic_year_id) || academic_year_id,
      class_id: Number(class_id) || class_id,
      section_id,
      student_id: student_id ? String(student_id) : "",
      start_date: startDate || "",
      end_date: endDate || "",
      fee_status: getStr("fee_status"),
      search: search || "",
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      school_id,
      academic_year_id,
      class_id,
      section_id,
      student_id,
      startDate,
      endDate,
      filterMap,
      search,
      pagination.pageIndex,
      pagination.pageSize,
    ], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ------------------------------------------------------------------
  // Fetch
  // ------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = buildPayload();
      const response = await AxiosInstance.post(
        "schoolFees/collectFee/list",
        payload,
      );

      if (response?.data?.status) {
        const rawList = response.data.data || [];
        const transformed = rawList.map((item, index) => ({
          id: `${item.admission_id || item.student_id || "row"}-${index}`,
          // fee record id from the API — forwarded to the detail screen
          // and used as `id` in the studentFeeView / paymentHistory payloads.
          fee_id: item.id,
          admission_id: item.admission_id || "",
          student_id: item.student_id,
          chalan_no: item.chellan_num || "",
          group_name: item.group_name || "",
          total_amount: item.total_amount || 0,
          net_amount: item.net_amount || 0,
          paid_amount: item.paid_amount || 0,
          balance_amount: item.balance_amount || 0,
          due_date: item.due_date || "",
          fee_status: item.fee_status || "",
          assign_status: Number(item.assign_status) || 0,
          // pass the payment history through for the accordion detail panel
          payment_history: (item.payment_history || []).map((p) => ({
            date: p.date || "-",
            payment_mode: p.payment_mode || "-",
            upi_transaction_id: p.upi_transaction_id || "-",
            paid: p.paid || 0,
            balance: p.balance || 0,
          })),
          _raw: item,
        }));

        setData(transformed);
        setRowCount(response.data.pagination?.total ?? transformed.length);
      } else {
        setData([]);
        setRowCount(0);
        setError(response?.data?.message || "Unable to load fees.");
      }
    } catch (e) {
      console.error("Pay fees list fetch error:", e);
      setData([]);
      setRowCount(0);
      setError(
        e?.response?.data?.message || "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [buildPayload]);

  // ------------------------------------------------------------------
  // Reset to page 0 whenever filters / search change
  // ------------------------------------------------------------------
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [columnFilters, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColumnFiltersChange = (updaterOrValue) => {
    setColumnFilters((prev) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue,
    );
  };

  const handleSearchEnter = (value) => {
    setSearch(value || "");
  };

  return {
    data,
    loading,
    error,
    rowCount,
    pagination,
    setPagination,
    searchInput,
    setSearchInput,
    handleSearchEnter,
    columnFilters,
    handleColumnFiltersChange,
    refetch: fetchData,
  };
}
